import { useEffect, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import {
  ActionBar,
  ChakraBox,
  ChakraFlex,
  ChakraImage,
  ChakraLink,
  Text,
  FAIconName,
  FireflyButton,
} from '@equip.health/ui';
import { Redirect } from 'react-router-dom';

import equipLogo from '~/assets/img/equipLogo.png';
import landingGraphic from '~/assets/img/landingPage.png';

import Loading from '~/pages/loading';

import { UserMode } from '../lib/contexts/ModeContext';
import useLocalStorage from '../lib/hooks/useLocalStorage';
import {
  bootstrapErrorKey,
  logInMethodKey,
  LoginMethod,
  Path,
} from '../routes/constants';
import util from '~/lib/util/util';
import {
  VITE_JUMPCLOUD_CONNECTION_NAME,
  VITE_MAUD_APP_VERSION,
} from '~/lib/constants/env';

const copy = {
  bootstrapErrorText:
    "We're sorry, your login failed. Please try again or contact support@equip.health if the issue continues.",
  copyrightText: '© 2026 Equip Health, Inc.',
  loginText: 'Log in with JumpCloud',
  supportEmail: 'support@equip.health',
  supportText: 'Need help? Please contact',
  useUsernameAndPasswordText: 'Use username and password',
  welcomeText: 'Welcome to Equip! Please use JumpCloud to log in below',
};

const SSOLogin = () => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const [hasBootstrapError, setHasBootstrapError] = useState<boolean>(false);
  const { setValue: setUserMode } = useLocalStorage<UserMode>(
    'userMode',
    UserMode.UNKNOWN,
  );
  const { setValue: setLoginMethod } = useLocalStorage<LoginMethod>(
    logInMethodKey,
    null,
  );

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setUserMode(UserMode.UNKNOWN);
    }
  }, [isLoading]);

  useEffect(() => {
    if (sessionStorage.getItem(bootstrapErrorKey) === null) return;
    sessionStorage.removeItem(bootstrapErrorKey);
    setHasBootstrapError(true);
  }, []);

  const appVersion = VITE_MAUD_APP_VERSION ? util.getAppVersion() : null;

  if (isLoading) return <Loading />;
  if (isAuthenticated) return <Redirect to={Path.HOME} />;

  return (
    <ChakraFlex
      alignItems="center"
      backgroundColor="primary.10"
      flexDirection="column"
      height="100%"
      justifyContent="center"
      width="100%"
    >
      <ChakraFlex
        alignItems="center"
        flexDirection="column"
        gap={{ base: '16px', md: '24px' }}
        justifyContent="center"
        px={{ base: 2, md: 0 }}
        width={{ base: '100%', md: '480px' }}
      >
        <ChakraImage
          alt="Equip Logo"
          height={{ base: '20px', md: '26px' }}
          src={equipLogo}
          width={{ base: '80px', md: '102px' }}
        />

        <ChakraBox>
          <ChakraImage
            alt="Landing Graphic"
            borderRadius={{ base: '32px 12px 0 0', md: '72px 24px 0 0' }}
            height={{ base: '60%', md: '220px' }}
            src={landingGraphic}
            width={{ base: '100%', md: '480px' }}
          />

          <ChakraBox
            alignItems="center"
            alignSelf="stretch"
            background="#FFF"
            borderRadius={{ base: '0 0 12px 12px', md: '0 0 24px 24px' }}
            display="flex"
            flexDirection="column"
            gap={{ base: '12px', md: '18px' }}
            padding={{ base: '12px 8px 16px 8px', md: '18px 18px 24px 18px' }}
          >
            <Text
              textAlign="center"
              variant="body"
              width={{ base: '100%', md: '444px' }}
            >
              {copy.welcomeText}
            </Text>
            {hasBootstrapError && (
              <ActionBar
                barIcon={FAIconName.exclamationTriangleRegular}
                id="sso-bootstrap-error"
                text={copy.bootstrapErrorText}
                variant="critical"
              />
            )}
            <FireflyButton
              data-testid="loginJumpCloudButton"
              id="loginJumpCloud"
              leftIcon={FAIconName.keySolid}
              onClick={() => {
                setLoginMethod(LoginMethod.JUMPCLOUD_SSO);
                loginWithRedirect({
                  authorizationParams: {
                    connection: VITE_JUMPCLOUD_CONNECTION_NAME,
                  },
                });
              }}
              rightIcon={FAIconName.angleRight}
              variant="primary"
            >
              {copy.loginText}
            </FireflyButton>

            <Text
              textAlign="center"
              variant="caption"
              width={{ base: '100%', md: '444px' }}
            >
              {copy.supportText}{' '}
              <ChakraLink
                color="primary.50"
                data-testid="supportEmail"
                href="mailto:support@equip.health"
                textDecoration="underline"
              >
                {copy.supportEmail}
              </ChakraLink>
            </Text>
          </ChakraBox>
        </ChakraBox>
        <ChakraFlex
          gap={{ base: '12px', md: '24px' }}
          justifyContent="center"
          margin="0 auto"
        >
          {appVersion && <Text variant="caption">{appVersion}</Text>}
          <Text variant="caption">{copy.copyrightText}</Text>
          <Text
            cursor="pointer"
            data-testid="useTestAccount"
            id="loginTestAccount"
            onClick={() => {
              setLoginMethod(LoginMethod.USERNAME_PASSWORD);
              loginWithRedirect({
                authorizationParams: {
                  connection: 'Username-Password-Authentication',
                },
              });
            }}
            variant="caption"
          >
            {copy.useUsernameAndPasswordText}
          </Text>
        </ChakraFlex>
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default SSOLogin;
